import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { getDocumentUrl } from '../../../services/documentService';

const UserDetailsModal = ({ user, onClose, onApprove, onReject }) => {
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [documentUrls, setDocumentUrls] = useState({});
  const [loadingUrls, setLoadingUrls] = useState(true);

  useEffect(() => {
    loadDocumentUrls();
  }, [user]);

  const loadDocumentUrls = async () => {
    if (!user?.documents?.length) {
      setLoadingUrls(false);
      return;
    }

    try {
      const urls = {};
      await Promise.all(
        user?.documents?.map(async (doc) => {
          try {
            const signedUrl = await getDocumentUrl(doc?.document_url);
            urls[doc.document_type] = signedUrl;
          } catch (error) {
            console.error(`Error loading ${doc?.document_type}:`, error);
            urls[doc.document_type] = null;
          }
        })
      );
      setDocumentUrls(urls);
    } catch (error) {
      console.error('Error loading document URLs:', error);
    } finally {
      setLoadingUrls(false);
    }
  };

  const handleApprove = () => {
    onApprove(user?.id);
    onClose();
  };

  const handleReject = () => {
    if (!rejectReason?.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    onReject(user?.id, rejectReason);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">{user?.name}</h2>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg hover:bg-muted transition-clinical flex items-center justify-center"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-start gap-6 mb-6">
            <Image
              src={user?.profileImage}
              alt={user?.profileImageAlt}
              className="w-24 h-24 rounded-lg object-cover"
            />
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-foreground mb-2">{user?.name}</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <Icon name="Mail" size={16} color="var(--color-muted-foreground)" />
                  <span className="text-sm text-foreground">{user?.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Phone" size={16} color="var(--color-muted-foreground)" />
                  <span className="text-sm text-foreground">{user?.phone}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="clinical-card p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Briefcase" size={16} color="var(--color-primary)" />
                Professional Information
              </h4>
              <div className="space-y-2">
                <div>
                  <span className="text-xs text-muted-foreground">Specialty</span>
                  <p className="text-sm font-medium text-foreground">{user?.specialty}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">License Number</span>
                  <p className="text-sm font-medium text-foreground">{user?.licenseNumber}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">License State</span>
                  <p className="text-sm font-medium text-foreground">{user?.licenseState}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Years of Experience</span>
                  <p className="text-sm font-medium text-foreground">{user?.yearsExperience} years</p>
                </div>
              </div>
            </div>

            <div className="clinical-card p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Building2" size={16} color="var(--color-accent)" />
                Institution Details
              </h4>
              <div className="space-y-2">
                <div>
                  <span className="text-xs text-muted-foreground">Institution Name</span>
                  <p className="text-sm font-medium text-foreground">{user?.institution}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Department</span>
                  <p className="text-sm font-medium text-foreground">{user?.department}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Location</span>
                  <p className="text-sm font-medium text-foreground">{user?.location}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Institution Type</span>
                  <p className="text-sm font-medium text-foreground">{user?.institutionType}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="clinical-card p-4 mb-6">
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="FileCheck" size={16} color="var(--color-success)" />
              Verification Status
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  user?.licenseVerified ? 'bg-success/10' : 'bg-warning/10'
                }`}>
                  <Icon 
                    name={user?.licenseVerified ? 'CheckCircle2' : 'Clock'} 
                    size={20} 
                    color={user?.licenseVerified ? 'var(--color-success)' : 'var(--color-warning)'} 
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">License Verification</p>
                  <p className="text-xs text-muted-foreground">
                    {user?.licenseVerified ? 'Verified' : 'Pending'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  user?.institutionVerified ? 'bg-success/10' : 'bg-warning/10'
                }`}>
                  <Icon 
                    name={user?.institutionVerified ? 'CheckCircle2' : 'Clock'} 
                    size={20} 
                    color={user?.institutionVerified ? 'var(--color-success)' : 'var(--color-warning)'} 
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Institution Verification</p>
                  <p className="text-xs text-muted-foreground">
                    {user?.institutionVerified ? 'Verified' : 'Pending'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="clinical-card p-4 mb-6">
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="FileText" size={16} color="var(--color-primary)" />
              Application Notes
            </h4>
            <p className="text-sm text-muted-foreground">{user?.applicationNotes}</p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Uploaded Documents</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="FileText" size={18} color="var(--color-primary)" />
                  <span className="text-sm font-medium text-foreground">Medical License</span>
                </div>
                {loadingUrls ? (
                  <div className="flex items-center justify-center py-8">
                    <Icon name="Loader2" size={24} color="var(--color-primary)" className="animate-spin" />
                  </div>
                ) : documentUrls?.medical_license ? (
                  <div className="space-y-3">
                    <div className="relative w-full h-48 bg-muted rounded-lg overflow-hidden">
                      <Image
                        src={documentUrls?.medical_license}
                        alt="Medical license document uploaded by user for verification"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ExternalLink"
                      iconPosition="right"
                      onClick={() => window.open(documentUrls?.medical_license, '_blank')}
                      fullWidth
                    >
                      View Full Document
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Document not available</p>
                )}
              </div>

              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Building2" size={18} color="var(--color-primary)" />
                  <span className="text-sm font-medium text-foreground">Clinical Affiliation</span>
                </div>
                {loadingUrls ? (
                  <div className="flex items-center justify-center py-8">
                    <Icon name="Loader2" size={24} color="var(--color-primary)" className="animate-spin" />
                  </div>
                ) : documentUrls?.clinical_affiliation ? (
                  <div className="space-y-3">
                    <div className="relative w-full h-48 bg-muted rounded-lg overflow-hidden">
                      <Image
                        src={documentUrls?.clinical_affiliation}
                        alt="Clinical affiliation document uploaded by user for institutional verification"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ExternalLink"
                      iconPosition="right"
                      onClick={() => window.open(documentUrls?.clinical_affiliation, '_blank')}
                      fullWidth
                    >
                      View Full Document
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Document not available</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex items-center justify-end gap-3">
          {showRejectForm ? (
            <div className="flex-1 flex items-center gap-3">
              <input
                type="text"
                placeholder="Enter rejection reason..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e?.target?.value)}
                className="flex-1 px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectForm(false);
                  setRejectReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                iconName="X"
                iconPosition="left"
                onClick={handleReject}
              >
                Confirm Reject
              </Button>
            </div>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button
                variant="destructive"
                iconName="X"
                iconPosition="left"
                onClick={() => setShowRejectForm(true)}
              >
                Reject
              </Button>
              <Button
                variant="default"
                iconName="CheckCircle2"
                iconPosition="left"
                onClick={handleApprove}
              >
                Approve Application
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;